import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { isEqual } from "lodash";
import {
  StringParam,
  createEnumParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { useEffectOnce } from "usehooks-ts";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import { useLogger } from "@components/v2/analytics";
import { type SearchQuery } from "@features/search-input/types";
import {
  CreativesSortingOrder,
  SavedSearchMethod,
  SavedSearchOperator,
  type CreativesStatusEnum,
  type FetchCustomGoals_me_customGoals_goals,
  type SalesTableReportSortingInput,
  type SalesTableReportV3MetricsFilterInput,
} from "@north-beam/nb-client-side";
import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  Granularity,
  MCAttributionWindowDays,
  NBAccountingMode,
  TimeGranularityV2,
  todayLocalAsISODate,
  type DateInterval,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
  type constants,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { type TabId } from "./tabs";
import { useResetTabStates } from "./tabs/hooks/use-reset-tabs-state";
import { type FlyoutTabId, type TopLevelBreakdown } from "./types";

export type PageConfiguration = {
  accountingMode: INBAccountingMode;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  breakdowns: TopLevelBreakdown[];
  compareDateRange: constants.CompareDateRange | null;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  filters: TopLevelBreakdown[];
  flyoutId?: string | null;
  flyoutTab: FlyoutTabId;
  granularity: IGranularity;
  metricFilters: SalesTableReportV3MetricsFilterInput;
  statuses: CreativesStatusEnum[] | null;
  searchQuery: SearchQuery;
  sorting: SalesSorting | null;
  sortingOrder: CreativesSortingOrder | null;
  timeGranularity: ITimeGranularityV2;
  activeTab: TabId;
};

export const AccountingModeParam = createEnumParam<
  PageConfiguration["accountingMode"]
>(NBAccountingMode.keyOrder);

export const AttributionWindowParam = createEnumParam<
  PageConfiguration["attributionWindow"]
>(MCAttributionWindowDays.keyOrder);

export const BreakdownParam =
  CustomJsonCompressedParam<PageConfiguration["breakdowns"]>();

export const CompareDateRangeParam =
  CustomJsonParam<PageConfiguration["compareDateRange"]>();

export const DateRangeParam = CustomJsonParam<PageConfiguration["dateRange"]>();

export const SalesSortingParam =
  CustomJsonCompressedParam<PageConfiguration["sorting"]>();

export const SalesSortingOrderParam =
  CustomJsonParam<PageConfiguration["sortingOrder"]>();

export const FlyoutTabParam = createEnumParam<PageConfiguration["flyoutTab"]>([
  "details",
  "performance",
  "creatives",
  "journey",
]);

export const GranularityParam = createEnumParam<
  PageConfiguration["granularity"]
>(Granularity.keyOrder);

export const MetricsFilterParam =
  CustomJsonCompressedParam<PageConfiguration["metricFilters"]>();

export const SearchQueryParam =
  CustomJsonCompressedParam<PageConfiguration["searchQuery"]>();

export const StatusesParam = CustomJsonParam<CreativesStatusEnum[] | null>();

export type SalesSorting = Omit<SalesTableReportSortingInput, "order"> & {
  id: string;
};

export const TimeGranularityParam = createEnumParam<
  PageConfiguration["timeGranularity"]
>(TimeGranularityV2.keyOrder);

export const DEFAULT_PAGE_CONFIGURATION: PageConfiguration = {
  accountingMode: "accrual",
  attributionModel: "",
  attributionWindow: "1",
  breakdowns: [],
  compareDateRange: null,
  dateRange: "last7Days",
  filters: [],
  flyoutId: null,
  flyoutTab: "details",
  granularity: "campaign",
  metricFilters: {} as SalesTableReportV3MetricsFilterInput,
  statuses: null,
  searchQuery: {
    id: undefined,
    method: SavedSearchMethod.text,
    operator: SavedSearchOperator.contains,
    query: "",
  },
  sorting: {
    id: "spend",
    customComposed: false,
    metric: {
      id: "spend",
      metric: "spend",
      details: {},
    },
    type: "base",
    order: "desc",
  } as SalesSorting,
  sortingOrder: CreativesSortingOrder.desc as CreativesSortingOrder,
  timeGranularity: "daily",
  activeTab: "breakdown",
};

export const ActiveTabParam = {
  encode: (tabId: TabId) => tabId,
  decode: (tabId: string | (string | null)[] | null | undefined) => {
    if (
      !tabId ||
      Array.isArray(tabId) ||
      !["breakdown", "campaign", "adset", "ad"].includes(tabId)
    ) {
      return "breakdown";
    }

    return tabId as TabId;
  },
};

export const isDefaultPageConfiguration = (pageConfig: PageConfiguration) =>
  isEqual(pageConfig, DEFAULT_PAGE_CONFIGURATION);

interface SalesV3ContextParams {
  customGoals: FetchCustomGoals_me_customGoals_goals[];
  isCustomGoalsDataLoading: boolean;
  pageConfiguration: PageConfiguration;
  setPageConfiguration(
    pageConfiguration:
      | PageConfiguration
      | ((pageConfiguration: PageConfiguration) => PageConfiguration),
    options?: any,
  ): void;
  statuses: CreativesStatusEnum[] | null;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  breakdowns: TopLevelBreakdown[];
  compareDateRange: constants.CompareDateRange | null;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  dateRangeInterval: DateInterval;
  filters: TopLevelBreakdown[];
  flyoutId?: string | null;
  flyoutTab: FlyoutTabId;
  granularity: IGranularity;
  metricFilters: SalesTableReportV3MetricsFilterInput;
  searchQuery: SearchQuery;
  sorting: SalesSorting | null;
  sortingOrder: CreativesSortingOrder | null;
  timeGranularity: ITimeGranularityV2;
  activeTab: TabId;
  setStatuses(value: CreativesStatusEnum[] | null): void;
  setSorting(value: SalesSorting | null): void;
  setSortingOrder(value: CreativesSortingOrder | null): void;
  setMetricFilters(metricFilters: SalesTableReportV3MetricsFilterInput): void;
  setActiveTab(tab: TabId): void;
  setGranularity(value: IGranularity): void;
  setGranularityAndActiveTab({
    granularity,
    activeTab,
  }: {
    granularity: IGranularity;
    activeTab: TabId;
  }): void;
  setAttributionModel(value: string): void;
  setAttributionWindow(value: IMCAttributionWindowDays): void;
  setAccountingMode(value: INBAccountingMode): void;
  setTimeGranularity(value: ITimeGranularityV2): void;
  setDateRange(value: FixedDateInterval | DateRangeChoiceV3): void;
  setCompareDateRange(value: constants.CompareDateRange | null): void;
  setBreakdowns(values: TopLevelBreakdown[]): void;
  setFilters(values: TopLevelBreakdown[]): void;
  setSearchQuery(value: SearchQuery): void;
  setFlyoutId(value: string | null): void;
  setFlyoutTab(value: FlyoutTabId): void;
}

export const SalesPageV3Context = createContext<SalesV3ContextParams | null>(
  null,
);

export const SalesPageV3Provider = ({
  defaultAccountingMode,
  children,
}: {
  defaultAccountingMode?: INBAccountingMode;
  children: ReactNode;
}) => {
  const { user } = useUser();
  const { resetAllStates } = useResetTabStates();
  const logger = useLogger({
    container: "breakdowns-management",
  });

  const [pageConfiguration, setPageConfiguration] = useQueryParams(
    {
      activeTab: useMemo(() => withDefault(ActiveTabParam, "breakdown"), []),
      attributionModel: useMemo(
        () => withDefault(StringParam, user.getDefaultAttributionModel("")),
        [user],
      ),
      attributionWindow: useMemo(
        () =>
          withDefault(
            AttributionWindowParam,
            DEFAULT_PAGE_CONFIGURATION.attributionWindow,
          ),
        [],
      ),
      statuses: useMemo(() => withDefault(StatusesParam, null), []),
      accountingMode: useMemo(
        () =>
          withDefault(
            AccountingModeParam,
            defaultAccountingMode ?? DEFAULT_PAGE_CONFIGURATION.accountingMode,
          ),
        [],
      ),
      timeGranularity: useMemo(
        () =>
          withDefault(
            TimeGranularityParam,
            DEFAULT_PAGE_CONFIGURATION.timeGranularity,
          ),
        [],
      ),
      dateRange: useMemo(
        () => withDefault(DateRangeParam, DEFAULT_PAGE_CONFIGURATION.dateRange),
        [],
      ),
      compareDateRange: useMemo(
        () =>
          withDefault(
            CompareDateRangeParam,
            DEFAULT_PAGE_CONFIGURATION.compareDateRange,
          ),
        [],
      ),
      breakdowns: useMemo(
        () =>
          withDefault(BreakdownParam, DEFAULT_PAGE_CONFIGURATION.breakdowns),
        [],
      ),
      filters: useMemo(
        () => withDefault(BreakdownParam, DEFAULT_PAGE_CONFIGURATION.filters),
        [],
      ),
      granularity: useMemo(
        () =>
          withDefault(GranularityParam, DEFAULT_PAGE_CONFIGURATION.granularity),
        [],
      ),
      flyoutId: withDefault(StringParam, DEFAULT_PAGE_CONFIGURATION.flyoutId),
      flyoutTab: useMemo(
        () => withDefault(FlyoutTabParam, DEFAULT_PAGE_CONFIGURATION.flyoutTab),
        [],
      ),

      searchQuery: useMemo(
        () =>
          withDefault(SearchQueryParam, DEFAULT_PAGE_CONFIGURATION.searchQuery),
        [],
      ),

      metricFilters: useMemo(
        () =>
          withDefault(
            MetricsFilterParam,
            DEFAULT_PAGE_CONFIGURATION.metricFilters,
          ),
        [],
      ),
      sorting: useMemo(
        () =>
          withDefault(SalesSortingParam, DEFAULT_PAGE_CONFIGURATION.sorting),
        [],
      ),
      sortingOrder: useMemo(
        () =>
          withDefault(
            SalesSortingOrderParam,
            DEFAULT_PAGE_CONFIGURATION.sortingOrder,
          ),
        [],
      ),
    },
    // batching breaks dnd kit sorting
    {
      enableBatching: false,
      skipUpdateWhenNoChange: true,
      updateType: "pushIn",
    },
  );

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const [wholeBreakdowns, setWholeBreakdowns] = useState<TopLevelBreakdown[]>(
    [],
  );
  useEffect(() => {
    const matchedWholeBreakdowns = pageConfiguration.breakdowns
      .map(({ key }) => {
        const matchedBreakdown = breakdownConfigs.find(
          (breakdown) => breakdown.key === key,
        );
        if (matchedBreakdown) {
          return {
            key: matchedBreakdown.key,
            values: matchedBreakdown.choices.map(({ value }) => value),
          };
        }

        return undefined;
      })
      .filter(Boolean) as TopLevelBreakdown[];

    setWholeBreakdowns(matchedWholeBreakdowns);
  }, [pageConfiguration.breakdowns, breakdownConfigs]);

  // set default values in URL
  useEffectOnce(() => {
    setPageConfiguration(pageConfiguration, "pushIn");
  });

  const dateRangeInterval = useMemo(
    () =>
      parseDateRangeArgument(
        pageConfiguration.dateRange,
        todayLocalAsISODate(),
      ),
    [pageConfiguration.dateRange],
  );

  const { customGoals, loading: isCustomGoalsDataLoading } =
    useCustomGoalsContext();

  const normalizedAttributionWindow = useMemo(() => {
    if (pageConfiguration.accountingMode === "cash") {
      return "infinity";
    }

    if (
      pageConfiguration.accountingMode === "accrual" &&
      pageConfiguration.attributionWindow === "infinity"
    ) {
      return "1";
    }

    return pageConfiguration.attributionWindow;
  }, [pageConfiguration.attributionWindow, pageConfiguration.accountingMode]);

  return (
    <SalesPageV3Context.Provider
      value={{
        customGoals,
        isCustomGoalsDataLoading,
        pageConfiguration: {
          ...pageConfiguration,
          attributionWindow: normalizedAttributionWindow,
        },
        setPageConfiguration,
        statuses: pageConfiguration.statuses,
        attributionModel: pageConfiguration.attributionModel,
        attributionWindow: normalizedAttributionWindow,
        accountingMode: pageConfiguration.accountingMode,
        timeGranularity: pageConfiguration.timeGranularity,
        dateRange: pageConfiguration.dateRange,
        dateRangeInterval: dateRangeInterval,
        compareDateRange: pageConfiguration.compareDateRange,
        breakdowns: wholeBreakdowns,
        filters: pageConfiguration.filters,
        granularity: pageConfiguration.granularity,
        searchQuery: pageConfiguration.searchQuery,
        flyoutId: pageConfiguration.flyoutId,
        flyoutTab: pageConfiguration.flyoutTab,
        sorting: pageConfiguration.sorting,
        sortingOrder: pageConfiguration.sortingOrder,
        metricFilters: pageConfiguration.metricFilters,
        activeTab: pageConfiguration.activeTab,
        setStatuses: useCallback(
          (value: CreativesStatusEnum[] | null) => {
            setPageConfiguration({ statuses: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setSorting: useCallback(
          (value: SalesSorting | null) => {
            setPageConfiguration({ sorting: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setSortingOrder: useCallback(
          (value: CreativesSortingOrder | null) => {
            setPageConfiguration({ sortingOrder: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setMetricFilters: useCallback(
          (metricFilters: SalesTableReportV3MetricsFilterInput) => {
            setPageConfiguration({ metricFilters }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setActiveTab: useCallback(
          (tab: TabId) => {
            setPageConfiguration({ activeTab: tab }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setGranularity: useCallback(
          (value: IGranularity) => {
            setPageConfiguration({ granularity: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setGranularityAndActiveTab: useCallback(
          ({
            granularity,
            activeTab,
          }: {
            granularity: IGranularity;
            activeTab: TabId;
          }) => {
            setPageConfiguration({ granularity, activeTab }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setAttributionModel: useCallback(
          (value: string) => {
            setPageConfiguration({ attributionModel: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setAttributionWindow: useCallback(
          (value: IMCAttributionWindowDays) => {
            setPageConfiguration({ attributionWindow: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setAccountingMode: useCallback(
          (value: INBAccountingMode) => {
            setPageConfiguration({ accountingMode: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setTimeGranularity: useCallback(
          (value: ITimeGranularityV2) => {
            /* Note: one of the limitations of hourly data processing is that we cannot
              process/store efficiently all historical data on an hourly grain. so we run
              attribution on the last 30 days worth of data only (no data prior to 30d),
              and report the last 7. */
            const modifiedPageConfiguration = {} as PageConfiguration;
            if (value === "hourly") {
              modifiedPageConfiguration.attributionWindow = "1";
            }
            setPageConfiguration(
              { ...modifiedPageConfiguration, timeGranularity: value },
              "pushIn",
            );
          },
          [setPageConfiguration],
        ),
        setDateRange: useCallback(
          (value: FixedDateInterval | DateRangeChoiceV3) => {
            setPageConfiguration({ dateRange: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setCompareDateRange: useCallback(
          (value: constants.CompareDateRange | null) => {
            setPageConfiguration({ compareDateRange: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setBreakdowns: useCallback(
          (values: TopLevelBreakdown[]) => {
            const breakdownKeysOnly = values.map(({ key }) => ({
              key,
              values: [],
            }));
            setPageConfiguration({ breakdowns: breakdownKeysOnly }, "pushIn");

            logger.event.salesPageUpdateBreakdowns("change", {
              eventDetails: { breakdowns: values },
            });
            resetAllStates();
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [setPageConfiguration],
        ),
        setFilters: useCallback(
          (values: TopLevelBreakdown[]) => {
            setPageConfiguration({ filters: values }, "pushIn");
            resetAllStates();
          },
          [setPageConfiguration],
        ),
        setSearchQuery: useCallback(
          (value: SearchQuery) => {
            setPageConfiguration({ searchQuery: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setFlyoutId: useCallback(
          (value: string | null) => {
            setPageConfiguration({ flyoutId: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
        setFlyoutTab: useCallback(
          (value: FlyoutTabId) => {
            setPageConfiguration({ flyoutTab: value }, "pushIn");
          },
          [setPageConfiguration],
        ),
      }}
    >
      {children}
    </SalesPageV3Context.Provider>
  );
};
